import { ACCESS, type AccessLevel, type User } from './types.js';

/**
 * Which navigation areas each access level may reach.
 *
 * This is shared with the front end so the sidebar can hide what a user cannot
 * open — but hiding is a courtesy, not a control. Every one of these areas is
 * re-checked server-side in the function allowlist, because the UI is not the
 * security boundary.
 */
/**
 * Sidebar sections, in the order they appear.
 *
 * Grouping is not decoration: an admin sees eleven destinations, and an
 * undivided list of eleven makes finding the one you want a reading exercise.
 * The section a page belongs to is also shown above its title.
 */
export const NAV_GROUPS = ['', 'My claims', 'Work queues', 'Reporting', 'Administration'] as const;
export type NavGroup = (typeof NAV_GROUPS)[number];

export interface NavItem {
  key: string;
  label: string;
  icon: string;
  /** The sidebar section this sits under. '' is the ungrouped item at the top. */
  group: NavGroup;
  /** Visible to every authenticated user. */
  all?: boolean;
  access?: AccessLevel[];
  /** Marks the whole group as restricted, not the single item. */
  restricted?: boolean;
}

export const NAV_MAP: NavItem[] = [
  { key: 'dashboard', label: 'Dashboard', icon: '🏠', group: '', all: true },
  { key: 'claim-new', label: 'New claim', icon: '🧾', group: 'My claims', all: true },
  { key: 'my-requests', label: 'My requests', icon: '📄', group: 'My claims', all: true },
  {
    key: 'approvals',
    label: 'Approvals',
    icon: '✅',
    group: 'Work queues',
    restricted: true,
    access: [
      ACCESS.MANAGER_APPROVER,
      ACCESS.LEAD_APPROVER,
      ACCESS.EXECUTIVE_APPROVER,
      ACCESS.MAIN_ACCESS,
    ],
  },
  // One finance destination since Round 2: review, approval and release are a
  // single stage worked by a single role.
  {
    key: 'finance-review',
    label: 'Finance review & approval',
    icon: '🔎',
    group: 'Work queues',
    access: [ACCESS.FINANCE_REVIEW, ACCESS.MAIN_ACCESS],
    restricted: true,
  },
  {
    key: 'financial-dashboard',
    label: 'Financial dashboard',
    icon: '📊',
    group: 'Reporting',
    access: [ACCESS.FINANCE_REVIEW, ACCESS.MAIN_ACCESS],
  },
  // Rates sit with Reporting rather than Administration: the people who
  // maintain them are Finance and Treasury, who never see the admin section.
  {
    key: 'exchange-rates',
    label: 'Exchange rates',
    icon: '💱',
    group: 'Reporting',
    access: [ACCESS.FINANCE_REVIEW, ACCESS.MAIN_ACCESS],
  },
  // Finance's own reference data, beside the exchange rates for the same
  // reason: the people who maintain it are Finance, who never see the
  // Administration section.
  {
    key: 'not-filed',
    label: 'Not yet filed',
    icon: '📭',
    group: 'Reporting',
    access: [ACCESS.FINANCE_REVIEW, ACCESS.MAIN_ACCESS],
  },
  {
    key: 'employee-finance',
    label: 'Allowances & SAP codes',
    icon: '💳',
    group: 'Reporting',
    access: [ACCESS.FINANCE_REVIEW],
  },
  {
    key: 'all-requests',
    label: 'All requests',
    icon: '📋',
    group: 'Administration',
    access: [ACCESS.MAIN_ACCESS],
  },
  {
    key: 'org-chart',
    label: 'Organization chart',
    icon: '🗂',
    group: 'Administration',
    access: [ACCESS.MAIN_ACCESS],
  },
  {
    key: 'access-mgmt',
    label: 'Access management',
    icon: '🔑',
    group: 'Administration',
    access: [ACCESS.MAIN_ACCESS],
  },
  {
    key: 'audit-trail',
    label: 'Audit trail',
    icon: '🕒',
    group: 'Administration',
    access: [ACCESS.MAIN_ACCESS],
  },
  { key: 'profile', label: 'Profile', icon: '👤', group: '', all: true },
];

export function navAllowed(item: NavItem, user: User): boolean {
  if (item.all) return true;
  return Boolean(item.access?.includes(user.systemAccess));
}

export function visibleNav(user: User): NavItem[] {
  return NAV_MAP.filter((item) => navAllowed(item, user));
}

export interface NavSection {
  group: NavGroup;
  restricted: boolean;
  items: NavItem[];
}

/**
 * The sidebar as sections, empty ones dropped.
 *
 * Built here rather than in the shell so the ordering and the "restricted"
 * marking are decided once, next to the permissions that produce them.
 */
export function navSections(user: User): NavSection[] {
  const visible = visibleNav(user);
  return NAV_GROUPS.map((group) => ({
    group,
    items: visible.filter((item) => item.group === group),
    restricted: visible.some((item) => item.group === group && item.restricted),
  })).filter((section) => section.items.length > 0);
}

/** Access levels permitted to act in each workflow queue. */
export const QUEUE_ACCESS = {
  approvals: [
    ACCESS.MANAGER_APPROVER,
    ACCESS.LEAD_APPROVER,
    ACCESS.EXECUTIVE_APPROVER,
    ACCESS.MAIN_ACCESS,
  ],
  /**
   * The single finance desk: review, approve and release.
   *
   * Segregation of duties narrows here, and deliberately — one role now both
   * sets the amount and records the payment. What still stands between a
   * person and their own money is that nobody may act on their own claim, in
   * the workflow module and in a CHECK constraint.
   */
  financeReview: [ACCESS.FINANCE_REVIEW, ACCESS.MAIN_ACCESS],
  financialDashboard: [ACCESS.FINANCE_REVIEW, ACCESS.MAIN_ACCESS],
  /**
   * Who maintains exchange rates. A rate decides how much money leaves the
   * company, so it belongs to the same desks that already decide amounts —
   * Finance and Treasury — plus Main Access.
   */
  exchangeRates: [ACCESS.FINANCE_REVIEW, ACCESS.MAIN_ACCESS],
  /**
   * Who may set an employee's monthly allowance and SAP vendor code.
   *
   * Finance only, and deliberately **not** Main Access: Finance asked for
   * fields that a directory administrator cannot change, because both decide
   * money and neither is a fact about the org chart. An administrator who
   * genuinely needs to set one can grant themselves Finance access, which is
   * audited — that is the difference between a role boundary and a secret.
   */
  employeeFinanceFields: [ACCESS.FINANCE_REVIEW],
  admin: [ACCESS.MAIN_ACCESS],
} as const satisfies Record<string, readonly AccessLevel[]>;

export function hasAccess(user: User, allowed: readonly AccessLevel[]): boolean {
  return allowed.includes(user.systemAccess);
}

export function isAdmin(user: User): boolean {
  return user.systemAccess === ACCESS.MAIN_ACCESS;
}
